import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { emailTemplateApi, statusEmailRuleApi } from '../services/api';
import {
  EmailTemplate,
  RESERVATION_STATUS_VALUES,
  ReservationStatusEmailRule,
  ReservationStatusEmailRuleRequest,
} from '../types/emailTemplates';
import { statusLabel } from '../i18n/translate';

const EMAIL_TEMPLATES_QUERY_KEY = 'emailTemplates';
const STATUS_EMAIL_RULES_QUERY_KEY = 'reservationStatusEmailRules';

export const EmailAutomationPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const templatesQuery = useQuery<EmailTemplate[]>(EMAIL_TEMPLATES_QUERY_KEY, () => emailTemplateApi.getAll());
  const templates = templatesQuery.data ?? [];
  const statusUpdateTemplates = templates.filter((template) => template.type === 'STATUS_UPDATE' && template.active);

  const statusRulesQuery = useQuery<ReservationStatusEmailRule[]>(STATUS_EMAIL_RULES_QUERY_KEY, () =>
    statusEmailRuleApi.getAll()
  );
  const [statusRules, setStatusRules] = useState<Record<string, { enabled: boolean; templateId: number | null }>>({});

  useEffect(() => {
    if (!statusRulesQuery.data) {
      return;
    }
    const next: Record<string, { enabled: boolean; templateId: number | null }> = {};
    statusRulesQuery.data.forEach((rule) => {
      next[rule.status] = { enabled: rule.enabled, templateId: rule.templateId };
    });
    setStatusRules(next);
  }, [statusRulesQuery.data]);

  const saveStatusRulesMutation = useMutation(
    () => {
      const requests: ReservationStatusEmailRuleRequest[] = RESERVATION_STATUS_VALUES.map((status) => ({
        status,
        enabled: statusRules[status]?.enabled ?? false,
        templateId: statusRules[status]?.templateId ?? null,
      }));
      return statusEmailRuleApi.save(requests);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(STATUS_EMAIL_RULES_QUERY_KEY);
        toast.success(t('emailTemplates.statusRules.saved', 'Automatic status emails saved'));
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || t('emailTemplates.statusRules.saveFailed', 'Failed to save automatic status emails')
        );
      },
    }
  );

  return (
    <Box>
      <Container maxWidth="xl" disableGutters sx={{ py: 2 }}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h6">
                {t('emailTemplates.statusRules.title', 'Automatic Emails by Status')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'emailTemplates.statusRules.description',
                  'When a reservation changes to one of these statuses, automatically send the selected Status Update template to the customer.'
                )}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={saveStatusRulesMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => saveStatusRulesMutation.mutate()}
              size="small"
              disabled={saveStatusRulesMutation.isLoading || statusRulesQuery.isLoading}
              sx={{ textTransform: 'none' }}
            >
              {t('common.save', 'Save')}
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{t('common.active', 'Active')}</TableCell>
                  <TableCell>{t('emailTemplates.statusRules.status', 'Status')}</TableCell>
                  <TableCell>{t('emailTemplates.statusRules.template', 'Template')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusRulesQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  RESERVATION_STATUS_VALUES.map((status) => {
                    const rule = statusRules[status] ?? { enabled: false, templateId: null };
                    return (
                      <TableRow key={status}>
                        <TableCell align="center">
                          <Switch
                            checked={rule.enabled}
                            onChange={(e) =>
                              setStatusRules((prev) => ({
                                ...prev,
                                [status]: { ...rule, enabled: e.target.checked },
                              }))
                            }
                          />
                        </TableCell>
                        <TableCell>{statusLabel(t, status)}</TableCell>
                        <TableCell sx={{ minWidth: 260 }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={rule.templateId ?? ''}
                            disabled={!rule.enabled}
                            onChange={(e) =>
                              setStatusRules((prev) => ({
                                ...prev,
                                [status]: {
                                  ...rule,
                                  templateId: e.target.value === '' ? null : Number(e.target.value),
                                },
                              }))
                            }
                          >
                            <MenuItem value="">
                              <em>{t('emailTemplates.statusRules.noTemplate', 'No template selected')}</em>
                            </MenuItem>
                            {statusUpdateTemplates.map((template) => (
                              <MenuItem key={template.id} value={template.id}>
                                {template.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
};
